/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  Typography,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  SvgIcon,
  Tabs,
  Tab,
  Box,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { withStyles } from 'tss-react/mui';
import Tooltip from 'src/components/Tooltip';
import { ReactComponent as DynamicFieldsIcon } from 'src/assets/images/dynamic-fields-icon-outlined.svg';
import helpers from 'src/helpers';
import { PERMISSIONS } from 'src/constants';
import { SF_ENTITY_LABELS } from 'src/constants/smartFieldLabels';
import { getSmartFieldDefinitionsForPicker } from 'src/actions/smartFieldsActions';

import selectFieldToInsertStyle from './selectFieldToInsert.style';

// CON-1635 — Dynamic Fields being retired from this picker. Keyed on the
// VALUE string, never the dynamicFields id (ids differ between QA and
// Production). The rows stay Active in the database so saved templates,
// action rules, and scheduled messages that already carry the token keep
// resolving at send time; only NEW insertion is blocked here. Applies to
// the Dynamic Fields tab only — Smart Fields are a different data source
// (pickerDefinitions) and are untouched.
const RETIRED_DYNAMIC_FIELD_VALUES = ['[Join Form URL]'];

// Kept to one line and appended after the value the row already shows. At
// insertion time the user needs the verdict and the alternative, nothing
// more; the long version buried the row in dimmed caption text.
const RETIRED_FIELD_NOTE = 'Retired. Use Insert Smart Form URL instead.';

const isRetiredDynamicField = (field) => RETIRED_DYNAMIC_FIELD_VALUES.includes(field?.value);

// Smart Fields category display order
const CATEGORY_ORDER = ['Host Info', 'Team Info', 'Shipping', 'Order', 'Custom'];

// Map entity type to token prefix
const ENTITY_PREFIX_MAP = {
  GroupMember: 'SFG',
  PartyGuest: 'SFE',
  Contact: 'SFC',
};

// Colored chip config per entity type (small/subtle)
const ENTITY_CHIP_CONFIG = {
  GroupMember: { label: 'Groups', bg: '#E3F2FD', color: '#1565C0' },
  PartyGuest: { label: 'Events', bg: '#FFF3E0', color: '#E65100' },
  Contact: { label: 'Contacts', bg: '#F3E5F5', color: '#7B1FA2' },
};

// ──────────────────────────────────────────────────────────────────────────────
// Inline style objects — duplicated from selectFieldToInsert.style.js so that
// production builds are immune to tss-react / MUI CSS-injection-order issues.
// ──────────────────────────────────────────────────────────────────────────────
const INLINE = {
  root: {
    position: 'relative',
    display: 'flex',
    border: '1px solid #C7C7C7',
    width: 32,
    height: 32,
    boxSizing: 'border-box',
    padding: 0,
    backgroundColor: '#ebebeb',
    borderRadius: 4,
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  divBtn: {
    display: 'flex',
    width: 35,
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropdown: {
    position: 'absolute',
    display: 'block',
    top: 35,
    left: -356,
    width: 390,
    maxHeight: 500,
    padding: 16,
    backgroundColor: '#ffffff',
    borderRadius: 4,
    border: '1px solid #C7C7C7',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    zIndex: 250,
    cursor: 'default',
  },
  buttonLine: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: 12,
    borderBottom: '1px solid #E0E0E0',
  },
  btnPrimary: {
    color: '#ffffff',
    background: '#5850EC',
    boxShadow: '0px 0px 0px 1px #5850EC',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 400,
    padding: '5px 20px',
    marginLeft: 8,
  },
  btnCancel: {
    color: '#ffffff',
    background: '#ABABB7',
    boxShadow: '0px 0px 0px 1px #ABABB7',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 400,
    padding: '5px 20px',
    marginLeft: 8,
  },
  formBox: {
    paddingTop: 12,
  },
  groupLabel: {
    color: '#393939',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '16px',
    padding: '0px 0px 10px 0px',
  },
  tabContent: {
    maxHeight: 320,
    overflowY: 'auto',
    border: '1px solid #E0E0E0',
    borderRadius: 4,
  },
  fieldList: {
    width: '100%',
    padding: 0,
  },
  sectionLabel: {
    display: 'block',
    padding: '8px 16px 4px',
    color: '#666',
    fontWeight: 600,
    backgroundColor: '#f9f9f9',
  },
  categoryHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
  },
  fieldItem: {
    borderBottom: '1px solid #f0f0f0',
  },
};

function SelectFieldToInsert({
  classes,
  addField,
  currentValue,
  screenName,
  showSmartFieldsTab,
  entityType,
  // CON-1621 — [{ entityType, reason }]. Those entity types' Smart Fields
  // are pulled out of the normal category list and rendered in their own
  // collapsed, non-selectable section showing the reason, so the user can
  // see the fields exist and learn why they can't be inserted here.
  disabledEntityTypes = [],
  externalOpen = null,
  onExternalOpenHandled = () => {},
}) {
  const dispatch = useDispatch();
  const [openSelect, setOpenSelect] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedField, setSelectedField] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownShiftX, setDropdownShiftX] = useState(0);
  const elRef = useRef(null);
  const dropdownRef = useRef(null);

  const { user } = useSelector((state) => state.account);
  const { pickerDefinitions, pickerLoading } = useSelector((state) => state?.smartFields || {});

  const {
    dynamicFields,
    textingProvider = 'PB',
  } = user;

  // Check Smart Fields permission
  const userRolePermissions = user?.userRole?.permissions || [];
  const hasSmartFieldsViewPermission = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_FIELDS_VIEW,
    userRolePermissions
  );

  // Only show Smart Fields tab if user has permission AND screen context allows it
  const shouldShowSmartFieldsTab = showSmartFieldsTab && hasSmartFieldsViewPermission;

  // Build the full list of entity types to fetch, always including Contact
  // alongside GroupMember or PartyGuest so Contact SFs are available too
  const allEntityTypes = React.useMemo(() => {
    const types = Array.isArray(entityType) ? entityType : [entityType];
    if (!types.includes('Contact')) {
      return [...types, 'Contact'];
    }
    return types;
  }, [entityType]);

  // Filter dynamic fields for CP
  const cpDynamicFields = React.useMemo(() => {
    const filtered = dynamicFields
      .filter((df) => (df?.screens?.includes('All') || df?.screens?.includes(screenName))
        && df?.softwareName === 'Connection Point')
      .map((df) => ({
        label: df.description,
        value: df.value,
      }));

    if (!searchTerm) return filtered;

    const lowerSearch = searchTerm.toLowerCase();
    return filtered.filter(
      (df) => df.label.toLowerCase().includes(lowerSearch)
        || df.value.toLowerCase().includes(lowerSearch)
    );
  }, [dynamicFields, screenName, searchTerm]);

  // Filter dynamic fields for PB
  const pbDynamicFields = React.useMemo(() => {
    const filtered = dynamicFields
      .filter((df) => (df?.screens?.includes('All') || df?.screens?.includes(screenName))
        && df?.softwareName === 'Project Broadcast')
      .map((df) => ({
        label: df.description,
        value: df.value,
      }));

    if (!searchTerm) return filtered;

    const lowerSearch = searchTerm.toLowerCase();
    return filtered.filter(
      (df) => df.label.toLowerCase().includes(lowerSearch)
        || df.value.toLowerCase().includes(lowerSearch)
    );
  }, [dynamicFields, screenName, searchTerm]);

  // CON-1621 — entityType -> reason, for the sections rendered read-only
  const disabledEntityTypeMap = React.useMemo(() => {
    const map = {};
    helpers.transform.toArray(disabledEntityTypes).forEach((item) => {
      if (item?.entityType) {
        map[item.entityType] = item.reason || '';
      }
    });
    return map;
  }, [disabledEntityTypes]);

  // Group Smart Fields by category and filter by search term
  const { groupedSmartFields, categoriesWithMatches } = React.useMemo(() => {
    if (!pickerDefinitions || pickerDefinitions.length === 0) {
      return { groupedSmartFields: {}, categoriesWithMatches: [] };
    }

    const grouped = {};
    const matchingCategories = [];
    const lowerSearch = searchTerm.toLowerCase();

    pickerDefinitions.forEach((def) => {
      // Disabled entity types render in their own section below, never here
      if (disabledEntityTypeMap[def.entityType]) return;

      const category = def.category || 'Custom';
      // Use displayLabel if available (company-specific), fall back to fieldLabel
      const label = def.displayLabel || def.fieldLabel;

      // If searching, only include matching fields
      if (searchTerm) {
        const matches = label.toLowerCase().includes(lowerSearch)
          || def.fieldKey.toLowerCase().includes(lowerSearch);
        if (!matches) return;
      }

      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(def);

      // Track categories that have matches
      if (searchTerm && !matchingCategories.includes(category)) {
        matchingCategories.push(category);
      }
    });

    return { groupedSmartFields: grouped, categoriesWithMatches: matchingCategories };
  }, [pickerDefinitions, searchTerm, disabledEntityTypeMap]);

  // CON-1621 — one read-only section per disabled entity type that actually
  // has fields available, so the explanation only appears when relevant.
  const disabledSmartFieldGroups = React.useMemo(() => {
    if (!pickerDefinitions || pickerDefinitions.length === 0) return [];
    return Object.keys(disabledEntityTypeMap)
      .map((disabledType) => ({
        entityType: disabledType,
        reason: disabledEntityTypeMap[disabledType],
        fields: pickerDefinitions.filter((def) => def.entityType === disabledType),
      }))
      .filter((group) => group.fields.length > 0);
  }, [pickerDefinitions, disabledEntityTypeMap]);

  // Get ordered categories that have definitions
  const orderedCategories = React.useMemo(
    () => CATEGORY_ORDER.filter((cat) => groupedSmartFields[cat]?.length > 0),
    [groupedSmartFields]
  );

  // Auto-expand categories with matches when searching
  React.useEffect(() => {
    if (searchTerm && categoriesWithMatches.length > 0) {
      const newExpanded = {};
      categoriesWithMatches.forEach((cat) => {
        newExpanded[cat] = true;
      });
      setExpandedCategories(newExpanded);
    }
  }, [searchTerm, categoriesWithMatches]);

  // CON-1621 — keep the panel inside whatever clips it.
  //
  // The panel is absolutely positioned from an anchor pinned to the right
  // edge of the message field's content box, and it overhangs that anchor
  // by ~34px (left: -356 with width: 390). That is invisible on screens
  // with room, but inside a dialog whose content is wider than the dialog
  // itself — the Modify Birthday Settings modal, where three nested MUI
  // Grid containers' negative margins push content past the dialog edge —
  // the anchor lands outside the dialog and MUI's DialogContent
  // (overflow-y: auto, which forces overflow-x to compute to auto rather
  // than visible) cuts the panel off. Measure once on open and shift left
  // by however much hangs past the nearest clipping ancestor, never so far
  // that the left edge goes off-screen. No-op wherever there is room, so
  // every existing picker renders exactly as before.
  const DROPDOWN_EDGE_MARGIN = 8;
  React.useLayoutEffect(() => {
    if (!openSelect || !dropdownRef.current) {
      setDropdownShiftX(0);
      return;
    }

    let boundaryRight = window.innerWidth;
    let node = dropdownRef.current.parentElement;
    while (node && node !== document.body) {
      const { overflowX } = window.getComputedStyle(node);
      if (overflowX && overflowX !== 'visible') {
        boundaryRight = Math.min(boundaryRight, node.getBoundingClientRect().right);
        break;
      }
      node = node.parentElement;
    }

    const { left, right } = dropdownRef.current.getBoundingClientRect();
    const overhang = right - (boundaryRight - DROPDOWN_EDGE_MARGIN);
    const roomToShift = Math.max(left - DROPDOWN_EDGE_MARGIN, 0);
    setDropdownShiftX(overhang > 0 ? -Math.min(overhang, roomToShift) : 0);
  }, [openSelect]);

  const shouldFieldBeClose = (e) => !helpers.hasParentClass(e.target, '.MuiAutocomplete-popper')
    && !helpers.hasParentClass(e.target, '.MuiChip-deleteIcon')
    && !helpers.hasParentClass(e.target, '.field-insert-dropdown');

  const handleClickAnywhere = (e) => {
    if (elRef?.current && !elRef.current.contains(e.target) && shouldFieldBeClose(e)) {
      setOpenSelect(false);
      setSelectedField(null);
      document.removeEventListener('click', handleClickAnywhere);
    }
  };

  const handleClick = () => {
    if (!openSelect) {
      // Fetch Smart Field definitions when opening (if tab is available)
      if (shouldShowSmartFieldsTab) {
        dispatch(getSmartFieldDefinitionsForPicker(allEntityTypes));
      }
      // Categories start collapsed
      setExpandedCategories({});
      document.addEventListener('click', handleClickAnywhere);
    } else {
      document.removeEventListener('click', handleClickAnywhere);
    }
    setOpenSelect(!openSelect);
    setSelectedField(null);
    setActiveTab(0);
    setSearchTerm('');
  };

  // Allow parent to programmatically open this picker
  useEffect(() => {
    if (externalOpen && !openSelect) {
      // Defer so the menu's click event finishes propagating before
      // we add the document click listener inside handleClick()
      setTimeout(() => {
        handleClick();
      }, 0);
      onExternalOpenHandled();
    }
  }, [externalOpen]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedField(null);
    setSearchTerm('');
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setSelectedField(null);
  };

  const handleCategoryToggle = (event, category) => {
    event.stopPropagation();
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleFieldSelect = (field, isSmartField = false) => {
    // CON-1635 — retired dynamic fields are never selectable. Smart Fields
    // are never retired, so the check only applies to the dynamic side.
    if (!isSmartField && isRetiredDynamicField(field)) {
      return;
    }
    setSelectedField({ ...field, isSmartField });
  };

  const handleClickApply = () => {
    // CON-1635 — final guard behind the disabled row and the disabled
    // Insert button: never insert a retired dynamic field value.
    if (selectedField && !selectedField.isSmartField && isRetiredDynamicField(selectedField)) {
      setOpenSelect(false);
      setSelectedField(null);
      return;
    }
    if (selectedField) {
      // Per-field prefix based on entityType of the definition
      const prefix = selectedField.isSmartField
        ? (ENTITY_PREFIX_MAP[selectedField.entityType] || 'SFG')
        : null;
      const token = selectedField.isSmartField
        ? `[${prefix}:${selectedField.displayLabel || selectedField.fieldLabel}]`
        : selectedField.value;
      addField(token, currentValue);
    }
    setOpenSelect(false);
    setSelectedField(null);
  };

  const handleClickCancel = () => {
    setOpenSelect(false);
    setSelectedField(null);
  };

  // CON-1635 — Insert stays disabled if a retired dynamic field somehow
  // became the selection (keyboard activation, or a future code path that
  // bypasses handleFieldSelect).
  const insertEnabled = !!selectedField
    && !(!selectedField.isSmartField && isRetiredDynamicField(selectedField));

  useEffect(() => () => document.removeEventListener('click', handleClickAnywhere), []);

  const tooltipText = shouldShowSmartFieldsTab
    ? 'Insert a Dynamic Field or Smart Field'
    : (textingProvider === 'PB' ? 'Insert a CP or PB Dynamic Field' : 'Insert a Dynamic Field');

  // Render Dynamic Fields list
  const renderDynamicFieldsList = () => {
    const noResults = cpDynamicFields.length === 0
      && (textingProvider !== 'PB' || pbDynamicFields.length === 0);

    if (searchTerm && noResults) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
          No matching fields found
        </Typography>
      );
    }

    return (
      <List component="nav" style={INLINE.fieldList} dense>
        {cpDynamicFields.length > 0 && (
          <>
            {textingProvider === 'PB' && (
              <Typography variant="caption" style={INLINE.sectionLabel}>
                CP Dynamic Fields
              </Typography>
            )}
            {cpDynamicFields.map((field) => (
              <ListItemButton
                key={field.value}
                onClick={() => handleFieldSelect(field, false)}
                selected={selectedField?.value === field.value && !selectedField?.isSmartField}
                style={INLINE.fieldItem}
                className={classes.fieldItem}
                disabled={isRetiredDynamicField(field)}
              >
                <ListItemText
                  primary={field.label}
                  secondary={
                    isRetiredDynamicField(field)
                      // CON-1635 — inline, not a tooltip: MUI blocks pointer
                      // events on disabled list items, so a hover affordance
                      // would never fire.
                      ? `${field.value} · ${RETIRED_FIELD_NOTE}`
                      : field.value
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption', color: 'primary' }}
                />
              </ListItemButton>
            ))}
          </>
        )}
        {textingProvider === 'PB' && pbDynamicFields.length > 0 && (
          <>
            <Typography variant="caption" style={{ ...INLINE.sectionLabel, marginTop: 16 }}>
              PB Dynamic Fields
            </Typography>
            {pbDynamicFields.map((field) => (
              <ListItemButton
                key={field.value}
                onClick={() => handleFieldSelect(field, false)}
                selected={selectedField?.value === field.value && !selectedField?.isSmartField}
                style={INLINE.fieldItem}
                className={classes.fieldItem}
                disabled={isRetiredDynamicField(field)}
              >
                <ListItemText
                  primary={field.label}
                  secondary={
                    isRetiredDynamicField(field)
                      ? `${field.value} · ${RETIRED_FIELD_NOTE}`
                      : field.value
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption', color: 'primary' }}
                />
              </ListItemButton>
            ))}
          </>
        )}
      </List>
    );
  };

  // CON-1621 — read-only sections for entity types the caller disabled.
  // Collapsed by default with the reason visible in the header; expanding
  // shows the fields greyed out and non-selectable.
  const renderDisabledSmartFieldSections = () => disabledSmartFieldGroups.map((group) => {
    const sectionKey = `disabled:${group.entityType}`;
    return (
      <React.Fragment key={sectionKey}>
        <ListItemButton
          onClick={(e) => handleCategoryToggle(e, sectionKey)}
          style={INLINE.categoryHeader}
          className={classes.categoryHeader}
        >
          <ListItemText
            primary={SF_ENTITY_LABELS[group.entityType] || group.entityType}
            secondary={group.reason}
            primaryTypographyProps={{
              variant: 'subtitle2',
              fontWeight: 600,
              color: 'text.disabled',
            }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
          {expandedCategories[sectionKey] ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={expandedCategories[sectionKey]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding dense>
            {group.fields.map((def) => {
              const label = def.displayLabel || def.fieldLabel;
              const prefix = ENTITY_PREFIX_MAP[def.entityType] || 'SFG';
              return (
                <ListItemButton
                  key={def.id}
                  disabled
                  style={{ ...INLINE.fieldItem, paddingLeft: 24 }}
                  className={classes.fieldItem}
                >
                  <ListItemText
                    primary={label}
                    secondary={`[${prefix}:${label}]`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      </React.Fragment>
    );
  });

  // Render Smart Fields list with collapsible categories
  const renderSmartFieldsList = () => {
    if (pickerLoading) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
          Loading...
        </Typography>
      );
    }

    const hasInsertableFields = orderedCategories.length > 0;
    const hasDisabledSections = disabledSmartFieldGroups.length > 0;

    if (!hasInsertableFields && !hasDisabledSections) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
          {searchTerm ? 'No matching smart fields found' : 'No smart fields available'}
        </Typography>
      );
    }

    return (
      <List component="nav" style={INLINE.fieldList} dense>
        {!hasInsertableFields && (
          <Typography variant="body2" color="textSecondary" sx={{ px: 2, py: 1 }}>
            {searchTerm ? 'No matching smart fields found' : 'No fields available to insert here'}
          </Typography>
        )}
        {orderedCategories.map((category) => (
          <React.Fragment key={category}>
            <ListItemButton
              onClick={(e) => handleCategoryToggle(e, category)}
              style={INLINE.categoryHeader}
              className={classes.categoryHeader}
            >
              <ListItemText
                primary={category}
                primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}
              />
              {expandedCategories[category] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={expandedCategories[category]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                {groupedSmartFields[category]?.map((def) => {
                  const label = def.displayLabel || def.fieldLabel;
                  const prefix = ENTITY_PREFIX_MAP[def.entityType] || 'SFG';
                  const chipConfig = ENTITY_CHIP_CONFIG[def.entityType];
                  return (
                    <ListItemButton
                      key={def.id}
                      onClick={() => handleFieldSelect({ ...def, value: `[${prefix}:${label}]` }, true)}
                      selected={selectedField?.id === def.id && selectedField?.isSmartField}
                      style={{ ...INLINE.fieldItem, paddingLeft: 24 }}
                      className={classes.fieldItem}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                      >
                        {chipConfig && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              px: 0.5,
                              py: 0,
                              mr: 1,
                              borderRadius: '8px',
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              lineHeight: '16px',
                              whiteSpace: 'nowrap',
                              minWidth: 48,
                              textAlign: 'center',
                              backgroundColor: chipConfig.bg,
                              color: chipConfig.color,
                            }}
                          >
                            {chipConfig.label}
                          </Box>
                        )}
                        <ListItemText
                          primary={label}
                          secondary={`[${prefix}:${label}]`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption', color: 'primary' }}
                        />
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
        {hasDisabledSections && renderDisabledSmartFieldSections()}
      </List>
    );
  };

  return (
    <div
      className={`${classes.root} withBtn inner`}
      ref={elRef}
      style={externalOpen !== null ? {
        ...INLINE.root,
        width: 0,
        height: 0,
        border: 'none',
        overflow: 'visible',
        backgroundColor: 'transparent',
      } : INLINE.root}
    >
      {externalOpen === null && (
      <Tooltip
        title={tooltipText}
        placement="left-end"
      >
        <div
          className="divBtn"
          style={INLINE.divBtn}
          onClick={handleClick}
        >
          <SvgIcon
            component={DynamicFieldsIcon}
            viewBox="0 0 22 22"
          />
        </div>
      </Tooltip>
      )}
      {openSelect && (
        <div
          ref={dropdownRef}
          className={`${classes.innerFieldDropDown} field-insert-dropdown`}
          style={dropdownShiftX
            ? { ...INLINE.dropdown, left: INLINE.dropdown.left + dropdownShiftX }
            : INLINE.dropdown}
        >
          <div style={INLINE.buttonLine}>
            <Button
              style={INLINE.btnCancel}
              onClick={handleClickCancel}
            >
              Cancel
            </Button>
            <Button
              style={insertEnabled ? INLINE.btnPrimary : { ...INLINE.btnPrimary, opacity: 0.5 }}
              onClick={handleClickApply}
              disabled={!insertEnabled}
            >
              Insert
            </Button>
          </div>
          <div style={INLINE.formBox}>
            {shouldShowSmartFieldsTab ? (
              <>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  className={classes.tabs}
                  variant="fullWidth"
                  sx={{
                    minHeight: 36,
                    mb: 1,
                    '& .MuiTab-root': {
                      minHeight: 36,
                      padding: '6px 12px',
                      fontSize: '13px',
                      textTransform: 'none',
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#5850EC',
                    },
                  }}
                >
                  <Tab label="Dynamic Fields" />
                  <Tab label="Smart Fields" />
                </Tabs>
                <TextField
                  size="small"
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={classes.searchField}
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-root': { fontSize: '13px', height: 32 },
                    '& .MuiInputBase-input': { padding: '6px 8px 6px 0' },
                    '& .MuiInputAdornment-root': { marginRight: '4px' },
                    '& .MuiSvgIcon-root': { color: '#888', fontSize: 18 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
                <Box style={INLINE.tabContent}>
                  {activeTab === 0 && renderDynamicFieldsList()}
                  {activeTab === 1 && renderSmartFieldsList()}
                </Box>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" style={INLINE.groupLabel}>
                  {textingProvider === 'PB'
                    ? 'Select Either a CP or PB Dynamic Field & Insert'
                    : 'Select a Dynamic Field & Insert'}
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={classes.searchField}
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-root': { fontSize: '13px', height: 32 },
                    '& .MuiInputBase-input': { padding: '6px 8px 6px 0' },
                    '& .MuiInputAdornment-root': { marginRight: '4px' },
                    '& .MuiSvgIcon-root': { color: '#888', fontSize: 18 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
                <Box style={INLINE.tabContent}>
                  {renderDynamicFieldsList()}
                </Box>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SelectFieldToInsert.propTypes = {
  classes: PropTypes.object.isRequired,
  addField: PropTypes.func.isRequired,
  currentValue: PropTypes.string.isRequired,
  screenName: PropTypes.string,
  showSmartFieldsTab: PropTypes.bool,
  entityType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  disabledEntityTypes: PropTypes.array,
  externalOpen: PropTypes.bool,
  onExternalOpenHandled: PropTypes.func,
};

SelectFieldToInsert.defaultProps = {
  screenName: 'Message Templates Edit',
  showSmartFieldsTab: false,
  // CON-1621 — fail safe. A caller that turns the Smart Fields tab on but
  // forgets entityType gets Contact fields (which resolve everywhere)
  // rather than Group fields (which silently blank outside a group).
  entityType: 'Contact',
};

export default withStyles(SelectFieldToInsert, selectFieldToInsertStyle, { withTheme: true });
